import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight, Clock, Facebook, Instagram, Youtube, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { KineticLines } from "../components/Kinetic";
import { Seo } from "../components/Seo";
import { BUSINESS, SOCIALS } from "../data/site";

const SOCIAL_ICONS = { Facebook, Instagram, YouTube: Youtube, "Find us on Google": Star };

export default function Contact() {
    return (
        <main data-testid="contact-page" className="pt-16">
            <Seo title="Contact | Lawn Busters Adelaide"
                description="Call Lawn Busters on 0481 831 282 or message us online. Servicing all Adelaide metro from the southern suburbs. Open Monday to Saturday." />
            <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
                <p className="eyebrow text-forest-leaf">Contact</p>
                <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
                    <KineticLines lines={["Talk To Your Local", "Adelaide Team"]} />
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-moss">
                    The quickest way to get started is a booking request — or give us a call and tell us what
                    your property needs.
                </p>
                <div className="mt-12 grid gap-6 lg:grid-cols-3">
                    <Reveal>
                        <a href={BUSINESS.phoneHref} data-testid="contact-phone-card"
                            className="block h-full rounded-3xl border border-forest/10 bg-bone-card p-8 transition-transform duration-300 hover:-translate-y-1">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Phone size={20} /></span>
                            <p className="eyebrow mt-6 text-forest-leaf">Call us</p>
                            <p className="mt-2 font-display text-2xl font-bold text-ink">{BUSINESS.phone}</p>
                            <p className="mt-2 text-sm text-moss">Tap to call — we&apos;ll talk through what you need.</p>
                        </a>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <Link to="/book-now" data-testid="contact-booking-card"
                            className="block h-full rounded-3xl border border-forest/10 bg-bone-card p-8 transition-transform duration-300 hover:-translate-y-1">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ochre text-forest-night"><Clock size={20} /></span>
                            <p className="eyebrow mt-6 text-forest-leaf">Booking request</p>
                            <p className="mt-2 font-display text-2xl font-bold text-ink">Book online</p>
                            <p className="mt-2 text-sm text-moss">
                                Pick 1, 2 or 3 services in one form — book directly, or request a free, no-obligation quote.{" "}
                                <span className="font-bold text-forest">Book Now <ArrowRight size={12} className="inline" /></span>
                            </p>
                        </Link>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <div data-testid="contact-area-card" className="h-full rounded-3xl border border-forest/10 bg-bone-card p-8">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest/10 text-forest"><MapPin size={20} /></span>
                            <p className="eyebrow mt-6 text-forest-leaf">Where we work</p>
                            <p className="mt-2 font-display text-2xl font-bold text-ink">{BUSINESS.city}</p>
                            <p className="mt-2 text-sm text-moss">Locally owned and operated. Proudly servicing Adelaide and surrounding suburbs.</p>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.2}>
                    <div data-testid="contact-social-strip" className="mt-10 flex flex-wrap items-center gap-4 rounded-3xl border border-forest/10 bg-bone-card p-6">
                        <p className="text-sm font-bold text-ink">Follow Lawn Busters</p>
                        <div className="flex flex-wrap gap-3">
                            {SOCIALS.map((s) => {
                                const Icon = SOCIAL_ICONS[s.name];
                                return (
                                    <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`contact-${s.testid}`}
                                        className="flex items-center gap-2 rounded-full border border-forest/15 bg-bone px-4 py-2 text-xs font-bold text-forest transition-colors hover:bg-forest hover:text-bone">
                                        <Icon size={14} /> {s.name}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </Reveal>
            </section>
            <section className="bg-bone-subtle py-16 lg:py-24" data-testid="contact-areas-section">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <p className="eyebrow text-forest-leaf">Areas we service</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">Adelaide &amp; surrounding suburbs</h2>
                        <p className="mt-4 max-w-xl text-base text-moss">
                            We service all of Adelaide, with the southern suburbs as our home base. Not sure if
                            we cover your area? Give us a call on {BUSINESS.phone} and we&apos;ll confirm.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {["All Adelaide Metro", "Flagstaff Hill", "Happy Valley", "Woodcroft", "Aberfoyle Park", "Hallett Cove", "Morphett Vale", "Christies Beach", "Seaford", "Glenelg"].map((chip) => (
                                <span key={chip} className="rounded-full border border-dashed border-forest/30 bg-bone px-4 py-2 text-xs font-semibold text-forest">
                                    {chip}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
